import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useData } from '../data/DataProvider';
import { useToast } from '../shared/components/ToastProvider';
import { date } from '../shared/formatters';
import { protectedFileUrl } from '../lib/pb';
import StatusPill from '../shared/components/StatusPill';

// Mint protected-file URLs at VIEW time with a fresh ~2min token —
// filenames alone live in state (file tokens expire far too fast to
// bake URLs; that was the photo 404 bug).
function usePhotoUrls(job) {
  const [urls, setUrls] = useState([]);
  useEffect(() => {
    let active = true;
    const filenames = job?.photos || [];
    if (!filenames.length) {
      setUrls([]);
      return undefined;
    }
    (async () => {
      try {
        const minted = await Promise.all(
          filenames.map((filename) => protectedFileUrl(
            { id: job.id, collectionName: 'jobs' },
            filename,
            { thumb: '300x300' },
          )),
        );
        if (active) setUrls(minted.filter(Boolean));
      } catch {
        if (active) setUrls([]);
      }
    })();
    return () => { active = false; };
  }, [job?.id, job?.photos?.join(',')]);
  return urls;
}

// Job detail (pass 6 §6B). A scheduled job offers "Request a reschedule" —
// the client picks a preferred date + note; the update flips status to
// reschedule_requested (pb_hook clamps + notifies the agency). Staff
// confirm by scheduling a new slot in the admin Schedule.
export default function JobDetail() {
  const { jobId } = useParams();
  const { data, update } = useData();
  const { notify } = useToast();
  const job = data.jobs.find((item) => item.id === jobId) || data.jobs[0];
  const [lightbox, setLightbox] = useState(null);
  const [rescheduling, setRescheduling] = useState(false);
  const [preferred, setPreferred] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const photoUrls = usePhotoUrls(job);

  const requestReschedule = async (event) => {
    event.preventDefault();
    if (!preferred && !note.trim()) {
      notify('Tell us a preferred date or what needs to change', 'error');
      return;
    }
    setBusy(true);
    try {
      await update('jobs', job.id, {
        status: 'reschedule_requested',
        client_requested_date: preferred || null,
        client_request_note: note.trim() || null,
      });
      setRescheduling(false);
      setPreferred('');
      setNote('');
      notify('Reschedule requested — we will confirm a new time with you.');
    } catch (err) {
      notify(err.message || 'Could not request the reschedule', 'error');
    } finally {
      setBusy(false);
    }
  };

  return <section className="portal-card job-detail">
    <span className="eyebrow">Work record</span>
    <h1>{job.title}</h1>
    <StatusPill status={job.status} />
    <p>{job.description}</p>
    <dl className="detail-list">
      <dt>Date</dt><dd>{date(job.date)}</dd>
      <dt>Engineer</dt><dd>{job.engineer}</dd>
      <dt>Notes</dt><dd>{job.notes || 'No additional notes.'}</dd>
      {job.status === 'reschedule_requested' && (
        <>
          <dt>Requested</dt>
          <dd>
            {job.client_requested_date ? `Prefers ${String(job.client_requested_date).slice(0, 10)}` : 'Flexible date'}
            {job.client_request_note ? ` — ${job.client_request_note}` : ''}
          </dd>
        </>
      )}
    </dl>
    {photoUrls.length ? (
      <div className="photo-gallery">
        {photoUrls.map((url) => (
          <button key={url} className="photo-thumb" onClick={() => setLightbox(url)}>
            <img src={url} alt={job.title} loading="lazy" />
          </button>
        ))}
      </div>
    ) : <div className="empty-state"><p>No photos attached.</p></div>}
    {lightbox && (
      <button className="photo-lightbox" aria-label="Close photo" onClick={() => setLightbox(null)}>
        <img src={lightbox} alt="Site photo" />
      </button>
    )}

    {job.status === 'scheduled' && !rescheduling && (
      <div className="invoice-actions">
        <button className="secondary-button" onClick={() => setRescheduling(true)}>Request a reschedule</button>
      </div>
    )}

    {job.status === 'scheduled' && rescheduling && (
      <form className="drawer-form" onSubmit={requestReschedule}>
        <p className="field-hint">We will do our best to match your preferred date — you will hear back before anything changes.</p>
        <label>Preferred date<input type="date" value={preferred} onChange={(event) => setPreferred(event.target.value)} /></label>
        <label>Why?<textarea rows="3" value={note} onChange={(event) => setNote(event.target.value)} placeholder="Work hours, access, holiday…" /></label>
        <div className="drawer-actions">
          <button type="button" className="secondary-button" onClick={() => setRescheduling(false)}>Cancel</button>
          <button className="primary-button" disabled={busy}>{busy ? 'Sending…' : 'Request reschedule'}</button>
        </div>
      </form>
    )}

    {job.status === 'reschedule_requested' && (
      <p className="field-hint">Your reschedule request is with the team — we will confirm a new time shortly.</p>
    )}
  </section>;
}
